import React, { useEffect, useMemo, useRef, useState } from "react";
import { MessageSquare, Send, Plus, Wifi, WifiOff, Paperclip, Video, X, Users, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ccInput, ccCard, ccBtnPink, ccBtnGhost, Avatar, EmptyState } from "./ui";

const API = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API.replace(/^http/, "ws") + "/api/ws/chat";

const fmtTime = (ts) => new Date(ts).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
import { openNativeMeeting } from "../modules/NativeMeeting";

const mediaSrc = (path) => `${API}/api/media/${path}`;
const ROLE_LABELS = { master: "NestTrack Admin", daycare_admin: "Daycare Admin", staff: "Staff", parent: "Parent", caregiver: "Caregiver" };

// Turn URLs in message text into clickable links
const Linkified = ({ text }) => {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return (
    <>
      {parts.map((p, i) => {
        if (!/^https?:\/\//.test(p)) return <span key={i}>{p}</span>;
        const meet = p.match(/[?&]meet=(nt-[a-z]+-[a-z0-9]+)/);
        if (meet) {
          return (
            <button key={i} type="button" onClick={() => openNativeMeeting(meet[1])} className="inline-flex items-center gap-1 underline font-extrabold" data-testid="chat-join-meeting">
              Join video meeting
            </button>
          );
        }
        return (
          <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="underline font-bold break-all">
            {p}
          </a>
        );
      })}
    </>
  );
};

const AttachmentView = ({ a }) => {
  if (a.kind === "video")
    return <video src={mediaSrc(a.path)} controls className="mt-1.5 rounded-2xl max-w-full max-h-64 bg-black" data-testid="chat-att-video" />;
  if (a.kind === "image")
    return <img src={mediaSrc(a.path)} alt={a.name} className="mt-1.5 rounded-2xl max-w-full max-h-64 object-cover" data-testid="chat-att-image" />;
  return (
    <a href={mediaSrc(a.path)} target="_blank" rel="noopener noreferrer" className="mt-1.5 flex items-center gap-1.5 text-xs font-bold underline" data-testid="chat-att-file">
      <FileText className="w-3.5 h-3.5" /> {a.name}
    </a>
  );
};

// Type-ahead recipient picker with multi-select → group chat when 2+ selected
const RecipientPicker = ({ onCreate, onCancel }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`${API}/api/chat/directory?q=${encodeURIComponent(q)}`, { credentials: "include" });
        setResults(res.ok ? await res.json() : []);
      } catch (err) {
        console.debug("directory search failed", err);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(debounce.current);
  }, [q]);

  const add = (u) => {
    if (selected.some((s) => s.email === u.email)) return;
    setSelected((prev) => [...prev, u]);
    setQ("");
    setResults([]);
  };

  return (
    <div className="mb-3 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl p-3 dark:bg-[#232D3D] dark:border-[#374457]" data-testid="chat-recipient-picker">
      <div className="flex gap-1.5 flex-wrap mb-2">
        {selected.map((s) => (
          <span key={s.email} className="flex items-center gap-1 px-2.5 py-1 bg-[#B3E0F2] rounded-full text-[11px] font-bold text-[#374151]" data-testid={`chat-chip-${s.email}`}>
            {s.name || s.email}
            <button type="button" onClick={() => setSelected((prev) => prev.filter((x) => x.email !== s.email))} data-testid={`chat-chip-remove-${s.email}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {selected.length > 1 && (
          <span className="flex items-center gap-1 px-2.5 py-1 bg-[#E2F4E1] border border-[#C4E5C2] rounded-full text-[10px] font-extrabold text-[#2F6031] uppercase" data-testid="chat-group-badge">
            <Users className="w-3 h-3" /> Group chat
          </span>
        )}
      </div>
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Type a name, email, or facility…"
        className={ccInput}
        data-testid="chat-recipient-search"
      />
      {searching && <p className="text-[10px] text-[#9CA3AF] font-bold mt-1.5 px-1">Searching…</p>}
      {!searching && results.length > 0 && (
        <div className="mt-1.5 max-h-40 overflow-y-auto space-y-1" data-testid="chat-directory-results">
          {results.map((u) => (
            <button
              key={u.email}
              type="button"
              onClick={() => add(u)}
              data-testid={`chat-dir-${u.email}`}
              className="w-full flex items-center gap-2.5 p-2 rounded-xl hover:bg-[#FCE6E9]/60 text-left transition-colors"
            >
              <Avatar name={u.name} size="w-7 h-7" />
              <span className="flex-1 min-w-0">
                <span className="block text-xs font-extrabold text-[#374151] dark:text-slate-100 truncate">{u.name || u.email}</span>
                <span className="block text-[10px] text-[#9CA3AF] font-medium truncate">
                  {ROLE_LABELS[u.role] || u.role}
                  {u.facilityName ? ` · ${u.facilityName}` : ""}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
      <div className="flex justify-end gap-2 mt-2.5">
        <button type="button" onClick={onCancel} className={ccBtnGhost} data-testid="chat-picker-cancel">
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onCreate(selected.map((s) => s.email))}
          disabled={selected.length === 0}
          className={`${ccBtnPink} disabled:opacity-50`}
          data-testid="chat-picker-start"
        >
          {selected.length > 1 ? `Start Group (${selected.length + 1})` : "Start Chat"}
        </button>
      </div>
    </div>
  );
};

// Live chat over WebSocket — recipient search, group threads, media attachments, meeting links
export default function LiveChat({ currentUser }) {
  const [threads, setThreads] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [wsState, setWsState] = useState("connecting");
  const wsRef = useRef(null);
  const activeRef = useRef(null);
  const retries = useRef(0);
  const listRef = useRef(null);
  const fileRef = useRef(null);

  const loadThreads = () =>
    fetch(`${API}/api/chat/threads`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((t) => {
        setThreads(t);
        return t;
      })
      .catch((err) => console.debug("threads fetch failed", err));

  useEffect(() => {
    let dead = false;
    let timer;
    const connect = () => {
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;
      ws.onopen = () => {
        retries.current = 0;
        setWsState("open");
        if (activeRef.current) ws.send(JSON.stringify({ type: "subscribe", thread_id: activeRef.current }));
      };
      ws.onmessage = (ev) => {
        let data;
        try {
          data = JSON.parse(ev.data);
        } catch {
          return;
        }
        if (data.type === "message") {
          const m = data.message;
          if (m.thread_id === activeRef.current) {
            setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
          }
          setThreads((prev) =>
            (prev || [])
              .map((t) => (t.id === m.thread_id ? { ...t, lastMessage: m.text.slice(0, 80) || "[attachment]", lastTs: m.ts } : t))
              .sort((a, b) => new Date(b.lastTs) - new Date(a.lastTs))
          );
        }
      };
      ws.onclose = () => {
        if (dead) return;
        setWsState("closed");
        retries.current += 1;
        if (retries.current <= 5) timer = setTimeout(connect, Math.min(1000 * 2 ** retries.current, 15000));
      };
      ws.onerror = () => ws.close();
    };
    connect();
    return () => {
      dead = true;
      clearTimeout(timer);
      wsRef.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadThreads().then((t) => {
      if (t && t.length && !activeRef.current) selectThread(t[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages]);

  const selectThread = async (tid) => {
    setActiveId(tid);
    activeRef.current = tid;
    wsRef.current?.readyState === 1 && wsRef.current.send(JSON.stringify({ type: "subscribe", thread_id: tid }));
    try {
      const res = await fetch(`${API}/api/chat/threads/${tid}/messages`, { credentials: "include" });
      setMessages(res.ok ? await res.json() : []);
    } catch (err) {
      console.debug("history fetch failed", err);
    }
  };

  const createThread = async (emails) => {
    try {
      const res = await fetch(`${API}/api/chat/threads`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emails }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not start conversation");
      setShowNew(false);
      await loadThreads();
      selectThread(body.id);
      toast.success(body.is_group ? "Group chat started!" : "Conversation started.");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/media/upload`, { method: "POST", credentials: "include", body: fd });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Upload failed");
      setAttachments((prev) => [...prev, { path: body.path, kind: body.kind, name: body.filename }]);
      toast.success(`${body.kind === "video" ? "Video" : body.kind === "image" ? "Photo" : "File"} attached.`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const addMeetingLink = async () => {
    try {
      const res = await fetch(`${API}/api/livekit/meet/create`, { method: "POST", credentials: "include" });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Video unavailable");
      setDraft((d) => (d ? `${d}\n${body.link}` : `Join my video meeting: ${body.link}`));
      openNativeMeeting(body.room);
      toast.success("Video meeting started — link added to your message.");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const deleteMessage = async (mid) => {
    if (!window.confirm("Delete this message? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API}/api/chat/threads/${activeThread.id}/messages/${mid}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Could not delete the message");
      setMessages((prev) => prev.map((x) => (x.id === mid ? { ...x, deleted: true, text: "", attachments: [] } : x)));
      toast.success("Message deleted");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if ((!text && !attachments.length) || !activeId) return;
    const payload = { text, attachments };
    if (wsRef.current?.readyState === 1) {
      wsRef.current.send(JSON.stringify({ type: "message", thread_id: activeId, ...payload }));
    } else {
      // REST fallback also broadcasts to WS subscribers
      try {
        const res = await fetch(`${API}/api/chat/threads/${activeId}/messages`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Send failed");
        setMessages((prev) => (prev.some((x) => x.id === body.id) ? prev : [...prev, body]));
      } catch (err) {
        toast.error(err.message);
        return;
      }
    }
    setDraft("");
    setAttachments([]);
  };

  const activeThread = useMemo(() => (threads || []).find((t) => t.id === activeId), [threads, activeId]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="cc-live-chat">
      {/* Thread list */}
      <div className={`${ccCard} p-4 md:col-span-1`}>
        <div className="flex items-center justify-between mb-3 px-1">
          <h3 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 flex items-center gap-1.5">
            Messages
            <span
              className={`flex items-center gap-1 text-[9px] font-extrabold uppercase px-2 py-0.5 rounded-full border ${wsState === "open" ? "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]" : "bg-[#FFF2D1] border-[#F5E3B3] text-[#7A5A17]"}`}
              data-testid="chat-ws-status"
            >
              {wsState === "open" ? <Wifi className="w-2.5 h-2.5" /> : <WifiOff className="w-2.5 h-2.5" />}
              {wsState === "open" ? "Live" : "Reconnecting"}
            </span>
          </h3>
          <button onClick={() => setShowNew(!showNew)} data-testid="chat-new-btn" className="w-7 h-7 rounded-full bg-[#F8C8DC] flex items-center justify-center text-[#374151] hover:bg-[#f3b4cd]">
            <Plus className="w-4 h-4" />
          </button>
        </div>
        {showNew && <RecipientPicker onCreate={createThread} onCancel={() => setShowNew(false)} />}
        {threads === null ? (
          <p className="text-xs text-[#9CA3AF] p-2">Loading conversations…</p>
        ) : threads.length === 0 ? (
          <p className="text-xs text-[#9CA3AF] p-2" data-testid="chat-empty">
            No conversations yet — start one with the + button.
          </p>
        ) : (
          <div className="space-y-1.5">
            {threads.map((t) => (
              <button
                key={t.id}
                onClick={() => selectThread(t.id)}
                data-testid={`chat-thread-${t.id}`}
                className={`w-full p-3 rounded-2xl flex items-center gap-3 text-left transition-colors duration-200 ${activeId === t.id ? "bg-[#FCE6E9]" : "hover:bg-[#FFF9F2] dark:hover:bg-[#273244]"}`}
              >
                {t.is_group ? (
                  <span className="w-10 h-10 rounded-full bg-[#E6F2FA] flex items-center justify-center shrink-0">
                    <Users className="w-4 h-4 text-[#5CB3DC]" />
                  </span>
                ) : (
                  <Avatar name={t.withName} size="w-10 h-10" />
                )}
                <div className="min-w-0">
                  <p className="text-xs font-extrabold text-[#374151] dark:text-slate-100 truncate">{t.withName || t.withEmail}</p>
                  <p className="text-[11px] text-[#9CA3AF] truncate">{t.lastMessage || "Start the conversation"}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Conversation */}
      <div className={`${ccCard} p-5 md:col-span-2 flex flex-col`} data-testid="chat-conversation">
        {activeThread ? (
          <>
            <div className="flex items-center gap-3 border-b border-[#FCE6E9] dark:border-[#374457] pb-3 mb-4">
              {activeThread.is_group ? (
                <span className="w-9 h-9 rounded-full bg-[#E6F2FA] flex items-center justify-center shrink-0">
                  <Users className="w-4 h-4 text-[#5CB3DC]" />
                </span>
              ) : (
                <Avatar name={activeThread.withName} size="w-9 h-9" />
              )}
              <div>
                <p className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100">{activeThread.withName || activeThread.withEmail}</p>
                <p className="text-[10px] text-[#9CA3AF] font-medium">
                  {activeThread.is_group ? (activeThread.members || []).map((m) => m.name || m.email).join(", ") : activeThread.withEmail}
                </p>
              </div>
            </div>
            <div ref={listRef} className="flex-1 space-y-3 max-h-96 overflow-y-auto pr-1 mb-4" data-testid="chat-message-list">
              {messages.length === 0 && <p className="text-xs text-[#9CA3AF] text-center py-6">Say hello — messages deliver instantly.</p>}
              {messages.map((m) => {
                const mine = m.sender_email === currentUser?.email;
                return (
                  <div key={m.id} className={`group flex items-center gap-1 ${mine ? "justify-end" : "justify-start"}`}>
                    {(mine || currentUser?.role === "master") && !m.deleted && (
                      <button onClick={() => deleteMessage(m.id)} title="Delete message" data-testid={`chat-delete-${m.id}`} className={`opacity-0 group-hover:opacity-100 transition-opacity w-6 h-6 rounded-full bg-white border border-[#FCE6E9] text-[#B04A6B] flex items-center justify-center hover:bg-[#FCE6E9] shrink-0 ${mine ? "order-2" : ""}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                    <div className={`max-w-[75%] px-4 py-2.5 rounded-3xl text-sm font-medium ${mine ? "bg-[#F8C8DC] text-[#374151] rounded-br-md" : "bg-[#E6F2FA] text-[#374151] rounded-bl-md dark:text-slate-800"}`}>
                      {activeThread.is_group && !mine && <p className="text-[9px] font-extrabold uppercase tracking-wide opacity-70 mb-0.5">{m.sender_name}</p>}
                      {m.deleted && <p className="italic opacity-60 text-xs" data-testid={`chat-deleted-${m.id}`}>Message deleted</p>}
                      {m.text && (
                        <p>
                          <Linkified text={m.text} />
                        </p>
                      )}
                      {(m.attachments || []).map((a) => (
                        <AttachmentView key={a.path} a={a} />
                      ))}
                      <p className="text-[9px] opacity-60 mt-0.5 text-right">{fmtTime(m.ts)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            {attachments.length > 0 && (
              <div className="flex gap-2 flex-wrap mb-2" data-testid="chat-attachment-tray">
                {attachments.map((a) => (
                  <span key={a.path} className="flex items-center gap-1.5 px-2.5 py-1 bg-[#E6F2FA] border border-[#B3E0F2] rounded-full text-[10px] font-bold text-[#3D7E9E]">
                    {a.kind === "video" ? <Video className="w-3 h-3" /> : a.kind === "image" ? <Paperclip className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                    {a.name}
                    <button type="button" onClick={() => setAttachments((prev) => prev.filter((x) => x.path !== a.path))}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <form onSubmit={send} className="flex gap-2">
              <input ref={fileRef} type="file" accept="image/*,video/*,.pdf" className="hidden" data-testid="chat-file-input" onChange={onPickFile} />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="px-3 py-2 bg-white hover:bg-[#E6F2FA] border border-[#B3E0F2] rounded-full text-[#3D7E9E] transition-colors disabled:opacity-50" data-testid="chat-attach-btn" title="Attach photo, video or file">
                <Paperclip className="w-4 h-4" />
              </button>
              <button type="button" onClick={addMeetingLink} className="px-3 py-2 bg-white hover:bg-[#E2F4E1] border border-[#C4E5C2] rounded-full text-[#2F6031] transition-colors" data-testid="chat-meeting-btn" title="Add a video meeting link">
                <Video className="w-4 h-4" />
              </button>
              <input type="text" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={uploading ? "Uploading attachment…" : "Type a message…"} className={ccInput} data-testid="chat-message-input" />
              <button type="submit" className={`${ccBtnPink} !px-4`} data-testid="chat-message-send">
                <Send className="w-4 h-4" />
              </button>
            </form>
          </>
        ) : (
          <EmptyState text="Select a conversation, or start a new one with the + button." testId="chat-no-thread" />
        )}
      </div>
    </div>
  );
}
